import React from 'react';
import _ from 'lodash';
import PolicyTabUpdateQuestion from '../ApplicationPolicy/policyTabUpdateQuestion';
import ModalWrapper from '../../helpers/modalWrapper';
import { FormGroup, Button, Loader, Alert, HorizontalRule, DragAndDrop, Link } from 'arag-ui-lib';
import AttachmentsList from '../General/attachmentsList';
import { FORM_GROUP_VARIANTS } from '../../helpers/formGroupVariants';
import { SIZE_LG, PRIMARY, TERTIARY, DOTS, SMALL, MEDIUM, ERROR, SUCCESS, DOTTED, RIGHT, LEFT } from '../../helpers/constants';
import { DROPZONE_ACCEPTED_FILES_ARRAY } from '../../helpers/variables';
import CONTENT_STRINGS from '../../helpers/contentStrings';

const ExternalPolicyUpdateModal = (props) => {
    const {
        isOpen,
        onClose,
        selectedPolicy,
        questions,
        fetching,
        formData,
        attachments,
        submitting,
        submitted,
        successMessage,
        errorMessage,
        handleFormCompChange,
        handleDateChange,
        handleSelectChange,
        onDrop,
        onDropMultiple,
        onDropSingle,
        handleRemoveAttachment,
        shouldUpdateQuestionBeMandatory,
        handleSubmit,
        handleDismissError,
    } = props;

    const footer = submitted ? (
        <Button
            variant={PRIMARY}
            label={"Close"}
            onClick={onClose}
            buttonTestId="external-policy-update-close-confirmation"
        />
    ) : (
        <div className="modal-footer-row">
            {errorMessage && (
                <div className="footer-alert-compact">
                    <Alert
                        variant={ERROR}
                        label={errorMessage}
                        onDismiss={handleDismissError}
                    />
                </div>
            )}
            <div className="modal-footer-actions">
                {submitting && <Loader variant={DOTS} dotSize={SMALL} />}
                <Link
                    variant={TERTIARY}
                    label={CONTENT_STRINGS.CANCEL}
                    arrowDirection={LEFT}
                    onClick={onClose}
                    linkTestId="external-policy-update-cancel"
                />
                <Button
                    variant={PRIMARY}
                    label={CONTENT_STRINGS.UPDATE}
                    arrowDirection={RIGHT}
                    onClick={handleSubmit}
                    disabled={submitting || fetching}
                    buttonTestId="external-policy-update-submit"
                />
            </div>
        </div>
    );

    return (
        <ModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            buttonTestId={"external-policy-update-close"}
            modalTestId={"external-policy-update-modal"}
            title={"External Policy Update"}
            size={SIZE_LG}
            footer={footer}
        >
            {submitted ? (
                <div data-testid="external-policy-update-confirmation">
                    <Alert variant={SUCCESS} label={successMessage || 'Your update has been received.'} />
                    <div className="external-policy-confirmation-details">
                        <FormGroup
                            labelSize={"col-sm-4"}
                            inputSize={"col-sm-6"}
                            label={"Policy Number"}
                            variant={FORM_GROUP_VARIANTS.PLAIN}
                            inputProps={{
                                label: selectedPolicy.policyNumber
                            }}
                            formGroupTestId={"external-policy-confirmation-number"}
                        />
                        <FormGroup
                            labelSize={"col-sm-4"}
                            inputSize={"col-sm-6"}
                            label={"Litigator Reference"}
                            variant={FORM_GROUP_VARIANTS.PLAIN}
                            inputProps={{
                                label: selectedPolicy.solicitorReference
                            }}
                            formGroupTestId={"external-policy-confirmation-reference"}
                        />
                        <FormGroup
                            labelSize={"col-sm-4"}
                            inputSize={"col-sm-6"}
                            label={"Insured"}
                            variant={FORM_GROUP_VARIANTS.PLAIN}
                            inputProps={{
                                label: selectedPolicy.clientName
                            }}
                            formGroupTestId={"external-policy-confirmation-insured"}
                        />
                    </div>
                </div>
            ) : (
                <>
                    {/* Policy Details Header */}
                    <FormGroup
                        labelSize={"col-sm-4"}
                        inputSize={"col-sm-6"}
                        label={"Policy Number"}
                        variant={FORM_GROUP_VARIANTS.PLAIN}
                        inputProps={{
                            label: selectedPolicy.policyNumber
                        }}
                        formGroupTestId={"external-policy-number"}
                    />
                    <FormGroup
                        labelSize={"col-sm-4"}
                        inputSize={"col-sm-6"}
                        label={"Litigator Reference"}
                        variant={FORM_GROUP_VARIANTS.PLAIN}
                        inputProps={{
                            label: selectedPolicy.solicitorReference
                        }}
                        formGroupTestId={"external-policy-litigator-reference"}
                    />
                    <FormGroup
                        labelSize={"col-sm-4"}
                        inputSize={"col-sm-6"}
                        label={"Insured"}
                        variant={FORM_GROUP_VARIANTS.PLAIN}
                        inputProps={{
                            label: selectedPolicy.clientName
                        }}
                        formGroupTestId={"external-policy-insured"}
                    />

                    <HorizontalRule variant={DOTTED} />

                    {/* Loading questions */}
                    {fetching && <Loader variant={DOTS} dotSize={MEDIUM} />}

                    {/* Dynamic questions */}
                    {/* WI 95280: pigApplied is passed as false - external policies have no PIG state of their
                        own, and this modal is driven by the externalPolicyUpdate slice, so the L&C lock that
                        PolicyTabUpdateQuestion applies for gATEway policies never applies here. */}
                    {!fetching && (
                        <form className="form-horizontal">
                            {_.sortBy(questions, ['sortOrder']).map(question =>
                                question.isTopLevel
                                ?
                                    <PolicyTabUpdateQuestion
                                        key={question.id}
                                        shouldUpdateQuestionBeMandatory={shouldUpdateQuestionBeMandatory}
                                        allQuestions={questions}
                                        question={question}
                                        formData={formData}
                                        HandleFormCompChange={handleFormCompChange}
                                        HandleDateChange={handleDateChange}
                                        OnDrop={onDrop}
                                        onDropMultiple={onDropMultiple}
                                        onDropSingle={onDropSingle}
                                        HandleSelectChange={handleSelectChange}
                                        pigApplied={false}
                                    />
                                :
                                    null
                            )}

                            <HorizontalRule variant={DOTTED} />

                            {/* File upload */}
                            <div className="form-group">
                                <label className="col-sm-4 upload-label">Upload Files (Max Size: 10MB)</label>
                                <div className="col-sm-6">
                                    <DragAndDrop
                                        label={'Try dropping some files here, or click to select files to upload.'}
                                        accepts={DROPZONE_ACCEPTED_FILES_ARRAY}
                                        allowMultiple={true}
                                        onDrop={(files) => onDrop(files)}
                                        required={false}
                                        ddTestId={'external-policy-DragAndDrop'}
                                    />
                                    <AttachmentsList
                                        attachments={attachments || []}
                                        onAttachmentRemove={(attachment, index) => handleRemoveAttachment(attachment, index)}
                                    />
                                </div>
                            </div>
                        </form>
                    )}

                </>
            )}
        </ModalWrapper>
    );
};

export default ExternalPolicyUpdateModal;
